import React, {Dispatch, SetStateAction} from "react";
import {cn} from "../ui/lib/utils";
import {defaultMultiMetricAlert} from "../../pages/alerts/MultiMetricAlert";
import {capitalizeFirstLetter} from "../../utility/stringUtils";
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "../ui/collapsible";
import {ChevronDown, ChevronRight} from "lucide-react";
import {defaultKubernetesResourceAlert, defaultMetricAlert} from "../../pages/alerts/MetricAlert";
import {defaultKubernetesAlert} from "../../pages/alerts/KubernetesAlert";
import {defaultLogAlert} from "../../pages/alerts/LogAlert";
import {defaultTraceAlert} from "../../pages/alerts/TraceAlert";
import {Alert, AlertType} from "../../pages/alerts/model";

export function AlertTypeSelector(
    props: {
        alert: Alert,
        setAlert: Dispatch<SetStateAction<Alert>>
    }) {
    const [isDeprecatedOpen, setIsDeprecatedOpen] = React.useState(false);

    // Automatically expand the deprecated section if a deprecated alert type is selected
    React.useEffect(() => {
        if (props.alert.type !== AlertType.MultiMetric) {
            setIsDeprecatedOpen(true);
        }
    }, [props.alert.type]);

    return <div className={"flex flex-col gap-4"}>
        <div className={"flex justify-start gap-2"}>
            <div
                className={"h-[36px] w-[36px] border border-primary bg-primarytransparent text-textlight flex flex-col justify-center text-center font-semibold rounded"}>
                1
            </div>
            <div className={"text-lg flex flex-col justify-center text-center text-textlight"}>
                Select Alert Type
            </div>
        </div>
        <div className={"flex flex-col gap-4"}>
            {/* Current Alert Types */}
            <div className={"flex justify-start"}>
                <div className={"flex gap-2 items-center"}>
                    <div
                        className={cn("h-[32px] flex justify-center items-center text-textmedium border gap-2 p-2 rounded bg-none border-buttonborder hover:text-textlight hover:border hover:border-secondary cursor-pointer px-4",
                            props.alert.type === AlertType.MultiMetric ? "border bg-secondarytransparenter border-secondary" : "")}
                        onClick={() => {
                            props.setAlert((prev) => {
                                return {...prev, alert: defaultMultiMetricAlert, type: AlertType.MultiMetric}
                            })
                        }}>
                        {capitalizeFirstLetter(AlertType.MultiMetric)}
                    </div>
                </div>
            </div>

            {/* Deprecated Alert Types */}
            <Collapsible open={isDeprecatedOpen} className="w-full">
                <CollapsibleTrigger
                    onClick={() => setIsDeprecatedOpen(!isDeprecatedOpen)}
                    className="flex items-center gap-2 text-sm text-destructive hover:text-destructive/80 cursor-pointer">
                    {isDeprecatedOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    Deprecated Alert Types
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <div className={"flex flex-wrap gap-2 mt-2"}>
                        {Object.values(AlertType)
                            .filter(type => type !== AlertType.MultiMetric)
                            .map((type, index) => {
                                return <div key={index}
                                            className={cn("h-[32px] flex justify-center items-center text-textmedium border gap-2 p-2 rounded bg-none border-buttonborder hover:text-textlight hover:border hover:border-secondary cursor-pointer px-4",
                                                "opacity-60 hover:opacity-80",
                                                props.alert.type === type ? "border bg-secondarytransparenter border-secondary" : "")}
                                            onClick={() => {
                                                if (type === AlertType.Metric) {
                                                    props.setAlert((prev) => {
                                                        return {...prev, alert: defaultMetricAlert}
                                                    })
                                                }

                                                if (type === AlertType.Kubernetes) {
                                                    props.setAlert((prev) => {
                                                        return {...prev, alert: defaultKubernetesAlert}
                                                    })
                                                }

                                                if (type === AlertType.Log) {
                                                    props.setAlert((prev) => {
                                                        return {...prev, alert: defaultLogAlert}
                                                    })
                                                }

                                                if (type === AlertType.Trace) {
                                                    props.setAlert((prev) => {
                                                        return {...prev, alert: defaultTraceAlert}
                                                    })
                                                }

                                                if (type === AlertType.KubernetesResource) {
                                                    props.setAlert((prev) => {
                                                        return {...prev, alert: defaultKubernetesResourceAlert}
                                                    })
                                                }

                                                props.setAlert((prev) => {
                                                    return {...prev, type: type as AlertType}
                                                })
                                            }}>
                                    <span className="flex items-center gap-1">
                                        {capitalizeFirstLetter(type)}
                                        <span className="text-xs text-destructive">(Deprecated)</span>
                                    </span>
                                </div>
                            })}
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </div>
    </div>;
}